import { motion } from "framer-motion";
import { Reveal, Counter } from "../components/motion";
import { Eyebrow } from "../components/CTA";
import PageHeader from "../components/sections/PageHeader";
import CTASection from "../components/sections/CTASection";
import EditorialMarquee from "../components/sections/EditorialMarquee";
import { COMPANY, STATS, ISO_CERTS } from "../lib/site";
import Seo from "../components/Seo";
import { Quote } from "lucide-react";

const CHAPTERS = [
  { n: "01", title: "Commitment to Quality", desc: "Every component is manufactured to exacting ISO-certified standards, verified through rigorous in-process and final inspection." },
  { n: "02", title: "Precision Manufacturing", desc: "Advanced Japanese CNC machinery delivers micron-level tolerances, consistent repeatability and superior surface finish." },
  { n: "03", title: "Customer Satisfaction", desc: "We build long-term partnerships through transparent communication, on-time delivery and dependable manufacturing quality." },
  { n: "04", title: "Advanced Technology", desc: "From fiber laser cutting to 9-stage powder coating, our facility is engineered around modern, automated production." },
  { n: "05", title: "Continuous Improvement", desc: "A culture of lean manufacturing and process optimisation keeps us competitive, efficient and future-ready." },
  { n: "06", title: "Long-Term Partnership", desc: "We grow with our clients — scaling capacity, capability and support as their manufacturing needs evolve." },
];

const MILESTONES = [
  { year: "Est.", title: "Founded in Neemrana", desc: "Precision sheet metal manufacturing begins at EPIP RIICO Industrial Area." },
  { year: "5+ Yrs", title: "Manufacturing Excellence", desc: "Building a reputation for precision, reliability and quality." },
  { year: "ISO", title: "Triple Certification", desc: "ISO 9001, 14001 & 45001 certified operations." },
  { year: "50+", title: "Skilled Workforce", desc: "A growing team of trained manufacturing professionals." },
];

const About = () => {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us"
        description="Learn about PAHI TECHNOCRATE PVT. LTD. — 5+ years of precision manufacturing excellence in Neemrana, Rajasthan. ISO 9001, 14001 & 45001 certified, 100+ skilled workforce, advanced Japanese machinery. Led by Managing Director Ajay Kumar."
        path="/about"
      />
      <PageHeader
        eyebrow="About PAHI"
        crumb="About"
        titleLines={["Precision is not a promise.", <span key="a" className="text-brand-soft">It is our process.</span>]}
        subtitle="PAHI TECHNOCRATE PVT. LTD. is a precision sheet metal manufacturing company in Neemrana, Rajasthan — delivering fabrication and machining components to OEMs and industrial leaders across India."
      />

      {/* Intro + leadership */}
      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-[1.3fr_1fr]">
          <Reveal>
            <Eyebrow>Our Story</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-medium leading-tight tracking-tight text-navy sm:text-4xl">
              Engineering trust through world-class manufacturing.
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600">
              <p>
                For over five years, PAHI TECHNOCRATE has manufactured high-precision sheet metal, fabrication and machining components for demanding industrial applications — from HVAC and automotive to electrical, telecom and EV.
              </p>
              <p>
                Our Neemrana facility integrates advanced Japanese machinery, an in-house tool room and a 9-stage powder coating plant under one roof, enabling complete control over quality, cost and delivery.
              </p>
              <p>
                Guided by ISO 9001, 14001 and 45001 standards, we combine modern manufacturing technology with a skilled engineering team to deliver components that perform — every time.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-navy/10 bg-navy/10 sm:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label} className="bg-white p-5">
                  <div className="font-display text-3xl font-bold text-navy">
                    <Counter value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-400">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl border border-navy/10 bg-gradient-to-b from-surface to-white">
              <div className="flex items-center justify-center bg-surface">
                <img src={COMPANY.ceo} alt="Ajay Kumar — Managing Director, PAHI Technocrate" className="max-h-[560px] w-full object-contain" loading="lazy" />
              </div>
              <div className="p-7">
                <Quote className="h-8 w-8 text-brand" />
                <p className="mt-4 font-display text-lg font-medium leading-relaxed text-navy">
                  "We don't just manufacture components — we engineer reliability that our partners can build their reputation on."
                </p>
                <div className="mt-6 border-t border-navy/10 pt-5">
                  <div className="font-display text-xl font-semibold text-navy">Ajay Kumar</div>
                  <div className="mt-0.5 text-sm font-semibold uppercase tracking-[0.15em] text-brand">Managing Director</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee dark />

      {/* Manifesto chapters */}
      <section className="bg-white py-24 sm:py-32" data-testid="manifesto-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="max-w-2xl">
            <Eyebrow>What Drives Us</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-medium tracking-tight text-navy sm:text-5xl">
              Six principles. One standard.
            </h2>
          </div>

          <div className="mt-16 divide-y divide-navy/10 border-y border-navy/10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={(i % 2) * 0.05}>
                <div className="group grid gap-6 py-10 md:grid-cols-[auto_1fr_2fr] md:items-center md:gap-12">
                  <div className="font-display text-6xl font-bold leading-none text-transparent [-webkit-text-stroke:1.5px_rgba(10,17,40,0.18)] transition-all duration-300 group-hover:[-webkit-text-stroke:1.5px_rgba(0,85,255,0.6)] sm:text-7xl">
                    {c.n}
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-navy">{c.title}</h3>
                  <p className="text-base leading-relaxed text-slate-500">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones timeline */}
      <section className="relative overflow-hidden bg-ink py-24 grain sm:py-32" data-testid="milestones-section">
        <div className="blueprint-grid absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex justify-center"><Eyebrow dark>Our Journey</Eyebrow></div>
          <h2 className="mt-5 text-center font-display text-4xl font-medium tracking-tight text-white sm:text-5xl">
            A track record of precision.
          </h2>
          <div className="mt-16 grid gap-5 md:grid-cols-4">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.08}>
                <div className="relative h-full rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                  <div className="font-display text-2xl font-bold text-brand-soft">{m.year}</div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-white">{m.title}</h3>
                  <p className="mt-2 text-sm text-white/55">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap justify-center gap-3">
            {ISO_CERTS.map((c) => (
              <span key={c.code} className="rounded-full border border-white/15 bg-white/5 px-5 py-2 text-sm font-semibold text-white/85 backdrop-blur-md">
                {c.code}
              </span>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default About;
